<template>
	<div>
		<!-- Displayed value -->
		<form-field-readonly
			@click="openModal"
			@remove:clicked="onUnselect"
			:hasRemove="computedValue ? true : false"
		>
			<!-- If no fact selected -->
			<div v-if="!computedValue">
				<slot name="empty">---</slot>
			</div>
			<!-- If fact selected, handle partial data missing with displaying N/A -->

			<div v-else>
				<!-- objectValue -->
				<div v-if="computedValue.scalarValueType === 'objectValue'" class="mt1">
					<div
						v-for="obj in computedValue.value"
						class="xs-12 lh125 mb1"
						:key="obj.key"
					>
						{{ obj.key }}:
						<div v-if="obj.value.objectValue">
							<div
								class="ml2"
								v-for="obj1 in obj.value.objectValue"
								:key="obj1.key"
							>
								{{ obj1.key }}:
								<span class="fw7">
									{{ obj1.value | extractFromScalarValueFilter | valueFilter }}
								</span>
							</div>
						</div>
						<span v-else class="fw7">
							{{ obj.value | extractFromScalarValueFilter | valueFilter }}
						</span>
					</div>
				</div>

				<!-- TODO: possibly arrayValue -->

				<!-- Simple values -->
				<div v-else>
					<span class="lh125">{{ computedValue.value | valueFilter }}</span>
					<!-- <span class="c6 ml1">
						({{ computedValue.type ? computedValue.type : "N/A" }})
					</span>-->
				</div>
			</div>
		</form-field-readonly>

		<!-- Modal -->
		<modal-simple
			v-if="showModal"
			@close="closeModal"
			:name="instanceKey"
			headline="Select Fact"
		>
			<div class="p4">
				<!-- Create new fact -->
				<div class="mb4 bb1 bc7">
					<div class="fs4 fw7 flex mb4">{{ $t("add_new_fact") }}</div>
					<entity-fact-create
						:createOptions="createOptions"
						:entityId="entityId"
						:entityName="entityName"
						@fact:created="onFactCreated"
					/>
				</div>
				<!-- Entity name and crete new -->
				<div class="xs-12 flex wrap mb4">
					<!-- Headline -->
					<div class="xs-8 lh125">
						<span class="fw7">{{ entityName }}</span>
						{{ $t("has_these_facts") }}:
					</div>
				</div>

				<!-- List of facts suggested facts to select from -->
				<entity-fact-list
					v-if="computedSuggested && computedSuggested.length"
					:facts="computedSuggested"
					:entityId="entityId"
					@item:clicked="onSelect($event.id)"
				/>

				<j-button
					v-if="computedNonSuggested && computedNonSuggested.length"
					type="tertiary"
					@click="toggleNonSuggestedFacts"
					>{{ showNonSuggestedfacts ? $t("see_less") : $t("see_more") }}
				</j-button>
				<!-- List of facts other facts to select from -->
				<entity-fact-list
					v-if="
						showNonSuggestedfacts &&
							computedNonSuggested &&
							computedNonSuggested.length
					"
					:facts="computedNonSuggested"
					:entityId="entityId"
					@item:clicked="onSelect($event.id)"
				/>

				<!-- No data -->
				<div
					v-if="!(computedList && computedList.length)"
					class="xs-12 bg5 r2 b1 bc7 p4 ta2 fs3 c6 fw7 mb4"
				>
					{{ $t("errors.no_data") }}
				</div>
			</div>
		</modal-simple>
	</div>
</template>

<script>
import JButton from "@/components/JButton"
import FormFieldReadonly from "../FormFieldReadonly.vue"
import JTag from "@/components/JTag"
import EntityFactCreate from "./EntityFactCreate"
import EntityFactList from "./EntityFactList"
import { valueFilter, extractFromScalarValueFilter } from "./utils"
import { getFieldData, shouldSuggest } from "../utils/index.js"
import ModalSimple from "../ModalSimple"

import dayjs from "dayjs"

export default {
	components: {
		JButton,
		FormFieldReadonly,
		JTag,
		EntityFactCreate,
		EntityFactList,
		ModalSimple
	},

	props: {
		/**
		 * Value represents `id`
		 */
		value: {
			type: String
		},

		/**
		 * Field from schema
		 * It's an object that inlcudes numerous properties, read docs for more details
		 * We compute some values based on those properties
		 */
		field: {
			type: Object,
			default: () => ({})
		},

		/**
		 * Options for filtering suggested facts
		 */
		suggest: {
			type: Object
		},

		/**
		 * Instructions for creation of a new fact
		 * What properties it can included is in docs
		 */
		createOptions: {
			type: Object
		}
	},

	data() {
		return {
			showNonSuggestedfacts: false,
			showCreateFact: false,
			instanceKey: Math.random().toString(), // get unique key fro modal window
			showModal: false
		}
	},

	computed: {
		/**
		 * Entity object that we get from API
		 * We get some other properties from this object that are crucial for some operations
		 * e.g. entityId, entityName, entityType via __typename
		 * thus it has to contain properties: `__typename`, `id` `name`
		 */
		entity() {
			const entity = getFieldData("entity", this.field, this)
			return entity
		},

		/**
		 * Based on this we do API calls
		 */
		entityId() {
			return this.entity?.id
		},

		/**
		 * Based on this we do API calls
		 */
		entityType() {
			return this.entity?.__typename
		},

		/**
		 * When we need to display name of an entity
		 */
		entityName() {
			return this.entity?.name
		},

		/**
		 * Will return data based on relation field
		 */
		cache() {
			return getFieldData("relation", this.field, this)
		},

		/**
		 * What user can select from, displayed in UI
		 */
		computedList() {
			// Helper to process scalarValues
			const processScalarValue = scalarValue => {
				if (!scalarValue) return { dataType: "", value: "" }

				return Object.keys(scalarValue)
					?.map(key => {
						if (scalarValue[key] !== null)
							return {
								dataType: key.replace("Value", ""),
								value: scalarValue[key]
							}
					})
					.filter(item => item)?.[0]
			}

			return this?.cache
				?.map(item => {
					const { value, dataType } = processScalarValue(item.value)
					return {
						...item,
						value,
						dataType,
						isSelected: item.id === this.value,
						isSuggested: shouldSuggest(item, this.suggest)
					}
				})
				.sort((a, b) => b.isSuggested - a.isSuggested) // sort based on suggested first
		},

		/*
		 * Only suggested facts
		 */
		computedSuggested() {
			return this.computedList?.filter(fact => fact.isSuggested)
		},

		/*
		 * Non-suggested facts
		 */
		computedNonSuggested() {
			return this.computedList?.filter(fact => !fact.isSuggested)
		},

		/**
		 * So that we do not display reference `id` but proper value
		 */
		computedValue() {
			const fact = this?.cache?.find(item => item?.id === this.value)
			// If no fact, return so that we can show no fact selected
			if (!fact) return

			// Determine what value to display based on what is not null
			const scalarValue = fact?.value
			const scalarValueType = scalarValue
				? Object.keys(scalarValue).filter(key => scalarValue[key] !== null)?.[0]
				: ""

			return {
				value:
					scalarValueType === "dateValue"
						? dayjs(Date.parse(scalarValue.dateValue)).format(
								this.$store.state?.settings?.dateFormat || "DD-MM-YYYY"
						  )
						: scalarValue?.[scalarValueType],
				type: fact?.type,
				scalarValueType // e.g. stringValue or objectValue...
			}
		}
	},

	methods: {
		// When user wants to open a picker modal,
		// check if if we actually have `entity` to pick data for
		// If not, show message and don't show modal
		openModal() {
			if (!this.entity) {
				this.$store.dispatch("addMessage", {
					delay: 1500,
					message: {
						type: "neutral",
						text: "You have to select entity first"
					}
				})
				return
			}

			this.showModal = true
		},

		closeModal() {
			this.showModal = false
		},

		toggleNonSuggestedFacts() {
			this.showNonSuggestedfacts = !this.showNonSuggestedfacts
		},

		onSelect(id) {
			this.$modal.hide(this.instanceKey)
			this.$emit("input", id)
		},

		// When user unselects
		onUnselect() {
			this.$emit("input", "")
			this.$modal.hide(this.instanceKey)
		},

		async onFactCreated(id) {
			this.$modal.hide("factCreate")

			// Update all entities in store by refetching the case graph
			await this.$store.dispatch("caseManagement/fetchCase", {
				caseId: this.$store.state.caseManagement.caseId
			})

			// Select fact automatically
			// Order is important, we do it after we update store
			this.onSelect(id)
		}
	},

	filters: {
		valueFilter,
		extractFromScalarValueFilter
	}
}
</script>
